import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

export interface ButtonDropdownOption {
  value: string | number;
  label: string;
}

@Component({
  selector: "app-button-dropdown",
  templateUrl: "./button-dropdown.component.html",
  styleUrls: ["./button-dropdown.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule],
})
export class ButtonDropdownComponent {
  /** Leading font-awesome icon classes, e.g. "fa fa-building-o". Optional. */
  @Input() leadIcon?: string;
  /** Tints the leading icon with the accent (green) color. */
  @Input() leadIconAccent = false;
  /** Options as plain strings or `{ value, label }` objects. */
  @Input() options: (string | ButtonDropdownOption)[] = [];
  /** Currently selected value (matched against option values). */
  @Input() selectedValue: string | number | null = null;
  /** Static text rendered before the selected label, e.g. "Auto Refresh: ". */
  @Input() labelPrefix = "";
  /** Overrides the computed label entirely when provided. */
  @Input() displayLabel?: string;
  /** Fallback text shown when nothing is selected. */
  @Input() placeholder = "Select";
  /** Menu horizontal alignment relative to the button. */
  @Input() menuAlign: "left" | "right" = "left";
  /** Minimum button width (any CSS length). Defaults to the live page width. */
  @Input() minWidth = "220px";
  @Input() minHeight = "36px";
  @Input() disabled = false;
  /** Stretch the control to fill its parent width. */
  @Input() block = false;
  /** Selected-item accent. Defaults to green; pass "#337ab7" for app blue. */
  @Input() accentColor = "#10b77f";
  /** Soft background for the active item (defaults derived from accent when unset). */
  @Input() accentSoftColor?: string;
  /** Shows a search box at the top of the menu that filters options by label. */
  @Input() searchable = false;
  /** Placeholder for the search box (only used when `searchable`). */
  @Input() searchPlaceholder = "Search...";
  /** Max height of the (scrollable) options list when `searchable`. */
  @Input() menuMaxHeight = "260px";

  @Output() selectionChange = new EventEmitter<string | number>();
  /** Emitted when the menu is opened (not when closed). */
  @Output() menuOpen = new EventEmitter<void>();

  open = false;
  searchTerm = "";

  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostBinding("class.ln-dropdown-host--block")
  get hostBlockClass(): boolean {
    return this.block || this.minWidth === "100%";
  }

  @HostBinding("style.--ln-dropdown-accent")
  get hostAccent(): string {
    return this.accentColor;
  }

  @HostBinding("style.--ln-dropdown-accent-soft")
  get hostAccentSoft(): string {
    if (this.accentSoftColor) {
      return this.accentSoftColor;
    }
    // Convert #RRGGBB to rgba(..., 0.12) for the soft active background.
    const hex = (this.accentColor || "").replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }
    return "rgba(16, 183, 127, 0.1)";
  }

  get normalizedOptions(): ButtonDropdownOption[] {
    return (this.options ?? []).map((option) =>
      typeof option === "string" ? { value: option, label: option } : option
    );
  }

  get filteredOptions(): ButtonDropdownOption[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.normalizedOptions;
    }
    return this.normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  }

  get buttonLabel(): string {
    if (this.displayLabel != null) {
      return this.displayLabel;
    }
    const selected = this.normalizedOptions.find(
      (option) => option.value === this.selectedValue
    );
    const base = selected ? selected.label : this.placeholder;
    return `${this.labelPrefix}${base}`;
  }

  get isPlaceholder(): boolean {
    if (this.displayLabel != null) {
      return false;
    }
    return (
      this.selectedValue === null ||
      this.selectedValue === undefined ||
      this.selectedValue === ""
    );
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
    if (this.open) {
      this.searchTerm = "";
      this.menuOpen.emit();
    }
  }

  close(): void {
    this.open = false;
    this.searchTerm = "";
  }

  select(option: ButtonDropdownOption): void {
    this.selectedValue = option.value;
    this.open = false;
    this.searchTerm = "";
    this.selectionChange.emit(option.value);
  }

  isActive(option: ButtonDropdownOption): boolean {
    return option.value === this.selectedValue;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.hostRef.nativeElement.contains(target)) {
      this.open = false;
      this.searchTerm = "";
    }
  }
}
